import { CalculatedValue } from '../../model/calculated-value';
import { ClientEvaluatedFactor } from '../../model/client-model/client-evaluated-factor';
import { ClientParameter } from '../../model/client-model/client-parameter';
import { KeyMap } from '../../services/climate-engine/ccc-algorithm/key-map';
import { SUPPLY_RESOURCE_GROUPS } from '../../services/climate-engine/ccc-algorithm/resource/resource';
import { ResourceData } from '../../services/climate-engine/model/calculation-result';
import { isClientParameter } from '../type-guards/client-model/is-client-parameter';

export function expressionIsVariableReference(expression: string, variableName: string): boolean {
  return !!expression && variableName === expression;
}

export function expressionToClientEvaluatedFactor(
  expression: string,
  unit: string,
  parameters: ClientParameter[]
): ClientEvaluatedFactor | undefined {
  const expressionData:
    | {
        expressionFunction: Function | undefined;
        expressionVariableNames: string[];
        expressionParameters: ClientParameter[];
      }
    | undefined = extractExpression(expression, parameters);

  if (!!expressionData) {
    const { expressionFunction, expressionVariableNames, expressionParameters } = { ...expressionData };
    let clientParameter: ClientParameter | undefined = undefined;
    if (expressionParameters?.length === 1 && expressionIsVariableReference(expression, expressionParameters[0].name)) {
      clientParameter = expressionParameters[0];
    }
    return {
      expression: expression,
      expressionFunction: expressionFunction!,
      expressionVariables: expressionVariableNames,
      expressionParameters,
      clientParameter,
      unit: unit,
    };
  }
}

export function extractExpression(
  expression: string,
  parameters: ClientParameter[]
):
  | {
      expressionFunction: Function | undefined;
      expressionVariableNames: string[];
      expressionParameters: ClientParameter[];
    }
  | undefined {
  let expressionFunction: Function | undefined = undefined;
  let expressionVariableNames: string[] = [];
  let expressionParameters: ClientParameter[] = [];
  if (expression !== undefined) {
    expressionVariableNames = _findParamsInStringFunction(expression);
    expressionParameters = _findClientParameters(expressionVariableNames, parameters);
    expressionFunction = Function(...expressionVariableNames, `return ${expression}`);
  }
  if (!!expressionFunction && expressionParameters.length === expressionVariableNames.length) {
    return { expressionFunction, expressionVariableNames, expressionParameters };
  }
}

//Note: The value of parameters where the unit is "percent" or "percentage" will be returned as a proportion.
export function findValue(
  valueContainer: ClientParameter | ClientEvaluatedFactor | CalculatedValue | undefined,
  metric: boolean = true
): number | undefined {
  const isEvaluatedFactor =
    typeof valueContainer === 'object' &&
    ('expressionParameters' in valueContainer || 'clientParameter' in valueContainer);
  if (valueContainer === undefined) {
    return undefined;
  } else if (isEvaluatedFactor) {
    if (!!valueContainer.expression) {
      return _evaluateFactor(valueContainer, metric);
    } else if (!!valueContainer.clientParameter) {
      return findValue(valueContainer.clientParameter, metric);
    } else {
      return 0;
    }
  } else if (isClientParameter(valueContainer)) {
    let parameterValue = 0;
    if (metric) {
      parameterValue = valueContainer.metricValue || 0;
    } else {
      parameterValue = valueContainer.value || 0;
    }
    return ['percent', 'percentage'].includes(valueContainer.unit) ? parameterValue / 100 : parameterValue;
  } else {
    const calculatedValue = valueContainer as CalculatedValue;
    let value = 0;
    if (metric) {
      value = calculatedValue?.metricValue || 0;
    } else {
      value = calculatedValue?.value || 0;
    }
    return ['percent', 'percentage'].includes(calculatedValue?.unit) ? value / 100 : value;
  }
}

export function getCalculatedEmissionFactors(resources: KeyMap<ResourceData>): KeyMap<CalculatedValue> {
  const calculatedEmissionFactors: KeyMap<CalculatedValue> = {};
  if (resources) {
    SUPPLY_RESOURCE_GROUPS.forEach((resource) => {
      const resourceData: ResourceData = resources[resource];
      if (resourceData) {
        const emissionFactorUnit: string = 'g_co2e_' + resourceData.unit;
        // NB: metricValue is needed in some places. Currently no calculated emission factors will have to be unit converted, so value and metricValue will always be the same.
        // No need to over-engineer this now, but if we need unit conversion of calculated emission factors, this needs to be updated.
        calculatedEmissionFactors[resource] = {
          value: resourceData.emissionFactor,
          metricValue: resourceData.emissionFactor,
          unit: emissionFactorUnit,
        };
      }
    });
  }
  return calculatedEmissionFactors;
}

export function getUnitOfFactor(factor: ClientEvaluatedFactor): string {
  if (factor?.clientParameter) {
    return factor.clientParameter.unit;
  } else if (factor?.expression) {
    return factor.unitOfMeasure || '';
  }
  return '';
}

function _evaluateFactor(factor: ClientEvaluatedFactor, metric: boolean = true): number {
  const expressionParameters = factor.expressionVariables.map((v) =>
    factor.expressionParameters?.find((p) => p.name === v)
  );
  if (expressionParameters.some((p) => p === undefined)) {
    return 0;
  }
  const values = expressionParameters.map((p) => findValue(p!, metric));
  const result = factor.expressionFunction(...values);
  return isFinite(result) ? result : 0;
}

function _findClientParameters(expressionVariableNames: string[], parameters: ClientParameter[]) {
  return expressionVariableNames
    .map((e) => {
      return parameters.find((p) => p.name === e);
    })
    .filter((d): d is ClientParameter => d !== undefined);
}

function _findParamsInStringFunction(value: string): string[] {
  return value ? value.split(' ').filter((paramName) => paramName.match(/[a-zA-Z]/)) : [];
}
